// src/pages/WorkingGenius.js
// Working Genius profile page for team_member and team_leader

import { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, collection, query, where, getDocs, serverTimestamp } from 'firebase/firestore/lite';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import PageLoading from '../components/PageLoading';

const GC = '#5f7942';
const FC = '#922b3f';

const GENIUSES = [
  { key: 'W', label: 'Wonder',      desc: 'Pondering possibilities and asking big questions' },
  { key: 'I', label: 'Invention',   desc: 'Creating original and novel solutions or ideas' },
  { key: 'D', label: 'Discernment', desc: 'Evaluating ideas with instinct and judgment' },
  { key: 'G', label: 'Galvanizing', desc: 'Rallying people to take action and inspiring movement' },
  { key: 'E', label: 'Enablement',  desc: 'Providing support and assistance to others' },
  { key: 'T', label: 'Tenacity',    desc: 'Pushing through to completion with discipline' },
];

function GeniusSelector({ title, subtitle, selected, max, onToggle, disabledKeys = [], type = 'genius' }) {
  const activeColor = type === 'genius' ? GC : FC;
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--navy)', marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: '0.8rem', color: 'var(--ink-muted)', marginBottom: 14 }}>{subtitle}</div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {GENIUSES.map(g => {
          const isSelected = selected.includes(g.key);
          const isDisabled = !isSelected && (selected.length >= max || disabledKeys.includes(g.key));
          return (
            <button
              key={g.key}
              disabled={isDisabled}
              onClick={() => onToggle(g.key)}
              title={g.desc}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 16px', borderRadius: 100,
                border: `2px solid ${isSelected ? activeColor : 'var(--border)'}`,
                background: isSelected ? activeColor : 'transparent',
                color: isSelected ? 'white' : isDisabled ? 'var(--ink-muted)' : 'var(--ink)',
                fontWeight: 700, fontSize: '0.85rem',
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                opacity: isDisabled ? 0.4 : 1,
                transition: 'all 0.15s', fontFamily: 'var(--font-body)',
              }}
            >
              <span style={{
                width: 22, height: 22, borderRadius: '50%',
                background: isSelected ? 'rgba(255,255,255,0.25)' : activeColor + '22',
                color: isSelected ? 'white' : activeColor,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.75rem', fontWeight: 800, flexShrink: 0,
              }}>{g.key}</span>
              {g.label}
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: '0.72rem', color: 'var(--ink-muted)', marginTop: 8 }}>
        {selected.length}/{max} selected
      </div>
    </div>
  );
}

function GeniusLetters({ geniuses, frustrations }) {
  return (
    <div className="wg-letters-row" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      {GENIUSES.map(g => {
        const isGenius = geniuses?.includes(g.key);
        const isFrustration = frustrations?.includes(g.key);
        return (
          <span key={g.key} title={g.label} style={{
            width: 26, height: 26, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.72rem', fontWeight: 800,
            background: isGenius ? GC : isFrustration ? FC + '22' : 'transparent',
            color: isGenius ? 'white' : isFrustration ? FC : 'var(--border)',
            border: isFrustration ? `2px dashed ${FC}` : isGenius ? 'none' : '2px solid var(--border)',
            flexShrink: 0,
          }}>{g.key}</span>
        );
      })}
    </div>
  );
}

export default function WorkingGenius() {
  const { currentUser, userProfile, effectiveUid, isImpersonating } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [myGeniuses, setMyGeniuses] = useState([]);
  const [myFrustrations, setMyFrustrations] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);

  useEffect(() => { loadData(); }, [currentUser, userProfile, effectiveUid]);

  async function loadData() {
    if (!currentUser) return;
    setLoading(true);
    try {
      const mySnap = await getDoc(doc(db, 'users', effectiveUid));
      if (mySnap.exists()) {
        const data = mySnap.data();
        setMyGeniuses(data.geniuses || []);
        setMyFrustrations(data.frustrations || []);
        // If profile already saved, start collapsed
        if ((data.geniuses || []).length === 2 && (data.frustrations || []).length === 2) {
          setIsEditing(false);
        } else {
          setIsEditing(true);
        }
      }
      const teamId = userProfile?.teamId;
      if (teamId) {
        const q = query(collection(db, 'users'), where('teamId', '==', teamId));
        const snap = await getDocs(q);
        setTeamMembers(
          snap.docs.map(d => ({ uid: d.id, ...d.data() }))
            .filter(m => m.uid !== effectiveUid)
            .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''))
        );
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function saveProfile() {
    if (!currentUser || isImpersonating) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        geniuses: myGeniuses, frustrations: myFrustrations, updatedAt: serverTimestamp(),
      });
      setSaved(true);
      setIsEditing(false);
      setTimeout(() => setSaved(false), 2500);
      await loadData();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  function toggleGenius(key) {
    setMyGeniuses(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    setSaved(false);
  }

  function toggleFrustration(key) {
    setMyFrustrations(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    setSaved(false);
  }

  const allMembers = [
    { uid: effectiveUid, displayName: userProfile?.displayName, geniuses: myGeniuses, frustrations: myFrustrations },
    ...teamMembers,
  ];

  const geniusCounts = {};
  const frustrationCounts = {};
  GENIUSES.forEach(g => { geniusCounts[g.key] = 0; frustrationCounts[g.key] = 0; });
  allMembers.forEach(m => {
    (m.geniuses || []).forEach(k => { if (geniusCounts[k] !== undefined) geniusCounts[k]++; });
    (m.frustrations || []).forEach(k => { if (frustrationCounts[k] !== undefined) frustrationCounts[k]++; });
  });

  const maxGeniusCount = Math.max(...Object.values(geniusCounts));
  const maxFrustrationCount = Math.max(...Object.values(frustrationCounts));
  const proneTo = maxGeniusCount > 0 ? GENIUSES.filter(g => geniusCounts[g.key] === maxGeniusCount) : [];
  const averseTo = maxFrustrationCount > 0 ? GENIUSES.filter(g => frustrationCounts[g.key] === maxFrustrationCount) : [];
  const membersWithProfiles = allMembers.filter(m => (m.geniuses || []).length > 0 || (m.frustrations || []).length > 0);

  if (loading) return <PageLoading text="Loading Working Genius..." />;

  const isComplete = myGeniuses.length === 2 && myFrustrations.length === 2;

  return (
    <div className="fade-up" style={{ maxWidth: 760 }}>
      <div className="report-header" style={{ marginBottom: 32 }}>
        <div className="report-header-meta">Working Genius</div>
        <h2>Your Genius Profile</h2>
        <div className="report-header-sub">
          Identify your Working Genius and Frustrations, then see how your team compares.
        </div>
      </div>

      <div className="card" style={{ marginBottom: 28 }}>
        {/* Collapsed summary bar — shown when profile is saved and not editing */}
        {!isEditing && isComplete ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '4px 0' }}>
            <div style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--navy)', minWidth: 80 }}>
              {userProfile?.displayName?.split(' ')[0] || 'You'}
            </div>
            <GeniusLetters geniuses={myGeniuses} frustrations={myFrustrations} />
            <div style={{ fontSize: '0.72rem', color: 'var(--ink-muted)', marginLeft: 4 }}>
              <span style={{ marginRight: 10 }}>● Genius</span>
              <span>◌ Frustration</span>
            </div>
            <button
              onClick={() => setIsEditing(true)}
              title="Edit your Working Genius"
              style={{
                marginLeft: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 32, height: 32, borderRadius: 8, border: '1.5px solid var(--border)',
                background: 'transparent', cursor: 'pointer', color: 'var(--ink-muted)',
                transition: 'all 0.15s', flexShrink: 0,
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--navy)'; e.currentTarget.style.color = 'var(--navy)'; e.currentTarget.style.background = 'var(--bg-soft)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--ink-muted)'; e.currentTarget.style.background = 'transparent'; }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          </div>
        ) : (
          /* Expanded editor */
          <>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 4 }}>
              <div>
                <div className="card-title" style={{ marginBottom: 4 }}>My Working Genius</div>
                <div className="card-subtitle">Select exactly 2 Geniuses and 2 Frustrations</div>
              </div>
              {isComplete && (
                <button
                  onClick={() => setIsEditing(false)}
                  title="Collapse"
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 30, height: 30, borderRadius: 8, border: '1.5px solid var(--border)',
                    background: 'transparent', cursor: 'pointer', color: 'var(--ink-muted)',
                    transition: 'all 0.15s', flexShrink: 0, marginTop: 2,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--navy)'; e.currentTarget.style.color = 'var(--navy)'; e.currentTarget.style.background = 'var(--bg-soft)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--ink-muted)'; e.currentTarget.style.background = 'transparent'; }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="18 15 12 9 6 15" />
                  </svg>
                </button>
              )}
            </div>
            <GeniusSelector title="My Geniuses" subtitle="The 2 types of work that give you energy and come naturally" selected={myGeniuses} max={2} onToggle={toggleGenius} disabledKeys={myFrustrations} type="genius" />
            <GeniusSelector title="My Frustrations" subtitle="The 2 types of work that drain you or feel unnatural" selected={myFrustrations} max={2} onToggle={toggleFrustration} disabledKeys={myGeniuses} type="frustration" />

            {(myGeniuses.length > 0 || myFrustrations.length > 0) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', background: 'var(--bg-soft)', borderRadius: 8, marginBottom: 16 }}>
                <div style={{ fontSize: '0.8rem', color: 'var(--ink-muted)', fontWeight: 600, minWidth: 80 }}>
                  {userProfile?.displayName?.split(' ')[0] || 'You'}
                </div>
                <GeniusLetters geniuses={myGeniuses} frustrations={myFrustrations} />
                <div style={{ fontSize: '0.72rem', color: 'var(--ink-muted)', marginLeft: 4 }}>● Genius &nbsp; ◌ Frustration</div>
              </div>
            )}

            <button onClick={saveProfile} disabled={saving || !isComplete || isImpersonating} title={isImpersonating ? 'Disabled while viewing as another user' : undefined} style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: saved ? GC : isComplete ? 'var(--navy)' : 'var(--border)', color: isComplete ? 'white' : 'var(--ink-muted)', fontWeight: 700, fontSize: '0.9rem', cursor: isComplete && !saving ? 'pointer' : 'not-allowed', transition: 'all 0.15s', fontFamily: 'var(--font-body)' }}>
              {saved ? '✓ Saved' : saving ? 'Saving...' : isComplete ? 'Save Profile' : 'Select 2 Geniuses + 2 Frustrations'}
            </button>
          </>
        )}
      </div>

      {teamMembers.length > 0 && (
        <>
          {/* Team Working Genius Map — moved to top */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--navy)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0 }}>🗺️</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--navy)' }}>Team Working Genius Map</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--ink-muted)' }}>Visual map of team members' Working Genius profiles</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, gridTemplateRows: 'auto' }} className="wg-map-grid">
              {GENIUSES.map(g => {
                const geniusMembers = membersWithProfiles.filter(m => (m.geniuses || []).includes(g.key));
                const frustrationMembers = membersWithProfiles.filter(m => (m.frustrations || []).includes(g.key));
                return (
                  <div key={g.key} style={{ borderRadius: 12, border: '1.5px solid var(--border)', padding: '16px', background: 'var(--bg-soft)', position: 'relative' }}>
                    <div style={{ position: 'absolute', top: 12, right: 12, width: 30, height: 30, borderRadius: '50%', background: 'var(--navy)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 800 }}>{g.key}</div>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--navy)', marginBottom: 12, paddingRight: 36 }}>{g.label}</div>
                    <div style={{ marginBottom: 10 }}>
                      <span style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '2px 8px', borderRadius: 100, background: GC + '22', color: GC, marginBottom: 6, display: 'inline-block' }}>Genius</span>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                        {geniusMembers.length > 0 ? geniusMembers.map(m => (
                          <span key={m.uid} style={{ fontSize: '0.75rem', fontWeight: 600, padding: '3px 8px', borderRadius: 100, background: GC + '18', color: GC, border: `1px solid ${GC}55` }}>{(m.displayName || '?').split(' ')[0]}</span>
                        )) : <span style={{ fontSize: '0.8rem', color: 'var(--ink-muted)' }}>—</span>}
                      </div>
                    </div>
                    <div>
                      <span style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '2px 8px', borderRadius: 100, background: FC + '18', color: FC, marginBottom: 6, display: 'inline-block' }}>Frustration</span>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                        {frustrationMembers.length > 0 ? frustrationMembers.map(m => (
                          <span key={m.uid} style={{ fontSize: '0.75rem', fontWeight: 600, padding: '3px 8px', borderRadius: 100, background: FC + '18', color: FC, border: `1px solid ${FC}33` }}>{(m.displayName || '?').split(' ')[0]}</span>
                        )) : <span style={{ fontSize: '0.8rem', color: 'var(--ink-muted)' }}>—</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {membersWithProfiles.length === 0 && (
              <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--ink-muted)', fontSize: '0.875rem' }}>
                No team members have set their Working Genius profile yet.
              </div>
            )}
          </div>

          {/* Team Overview */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--navy)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0 }}>👥</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--navy)' }}>Team Overview</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--ink-muted)' }}>Manage team members and their information</div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {allMembers.map((m, i) => {
                const isMe = m.uid === effectiveUid;
                const hasProfile = (m.geniuses || []).length > 0 || (m.frustrations || []).length > 0;
                return (
                  <div key={m.uid || i} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 4px', borderBottom: i < allMembers.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem', minWidth: 110, color: isMe ? 'var(--navy)' : 'var(--ink)' }}>
                      {(m.displayName || 'Unknown').split(' ')[0]}
                      {isMe && <span style={{ fontSize: '0.65rem', color: 'var(--ink-muted)', fontWeight: 400, marginLeft: 6 }}>(you)</span>}
                    </div>
                    {hasProfile
                      ? <GeniusLetters geniuses={m.geniuses} frustrations={m.frustrations} />
                      : <div style={{ display: 'flex', gap: 6 }}>
                          {GENIUSES.map(g => (
                            <span key={g.key} style={{ width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 800, color: '#ccc', border: '2px solid #eee' }}>{g.key}</span>
                          ))}
                        </div>
                    }
                    {!hasProfile && <span style={{ fontSize: '0.72rem', color: 'var(--ink-muted)', marginLeft: 4 }}>Not set</span>}
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 20, marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)', fontSize: '0.75rem', color: 'var(--ink-muted)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><div style={{ width: 14, height: 14, borderRadius: '50%', background: GC }} />Genius (filled)</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><div style={{ width: 14, height: 14, borderRadius: '50%', border: `2px dashed ${FC}`, background: FC + '18' }} />Frustration (dashed)</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><div style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid #eee' }} />Not set</div>
            </div>
          </div>

          {/* Team Genius Tendencies */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#e8f0fe', color: '#4361ee', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0 }}>⚖️</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--navy)' }}>Team Genius Tendencies</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--ink-muted)' }}>Most common genius strengths and frustrations</div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              <div style={{ padding: '20px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-muted)', marginBottom: 12 }}>PRONE TO</div>
                {proneTo.length > 0 ? (
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {proneTo.map(g => (
                      <div key={g.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 30, height: 30, borderRadius: '50%', background: GC, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 800, flexShrink: 0 }}>{g.key}</div>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--ink)' }}>{g.label}</div>
                          <div style={{ fontSize: '0.72rem', color: 'var(--ink-muted)' }}>{geniusCounts[g.key]} team member{geniusCounts[g.key] !== 1 ? 's' : ''}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <div style={{ fontSize: '0.875rem', color: 'var(--ink-muted)', fontStyle: 'italic' }}>No clear tendency</div>}
              </div>
              <div style={{ paddingTop: 20 }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-muted)', marginBottom: 12 }}>AVERSE TO</div>
                {averseTo.length > 0 ? (
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {averseTo.map(g => (
                      <div key={g.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 30, height: 30, borderRadius: '50%', background: FC + '22', border: `2px dashed ${FC}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 800, color: FC, flexShrink: 0 }}>{g.key}</div>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--ink)' }}>{g.label}</div>
                          <div style={{ fontSize: '0.72rem', color: 'var(--ink-muted)' }}>{frustrationCounts[g.key]} team member{frustrationCounts[g.key] !== 1 ? 's' : ''}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <div style={{ fontSize: '0.875rem', color: 'var(--ink-muted)', fontStyle: 'italic' }}>No clear tendency</div>}
              </div>
            </div>
          </div>

        </>
      )}

      {teamMembers.length === 0 && !loading && (
        <div style={{ padding: '24px', background: 'var(--bg-soft)', borderRadius: 12, textAlign: 'center', color: 'var(--ink-muted)', fontSize: '0.875rem' }}>
          You're not currently assigned to a team. Team Genius data will appear here once you join one.
        </div>
      )}
    </div>
  );
}
